import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api, Job } from '../../lib/api';
import { usePagination } from '../../hooks/usePagination';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import { useTrades } from '../../hooks/useTrades';
import { Pagination } from '../shared/Pagination';
import { JobCardSkeleton } from '../shared/LoadingSkeleton';
import { EmptyState } from '../shared/EmptyState';
import { PullToRefreshIndicator } from '../shared/PullToRefresh';
import {
  MapPin,
  DollarSign,
  Calendar,
  Shield,
  CheckCircle2,
  Search,
  SlidersHorizontal,
  Map,
  List,
  Briefcase
} from 'lucide-react';

export const JobMarketplace: React.FC = () => {
  const { profile } = useAuth();
  const { trades } = useTrades();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [filters, setFilters] = useState({
    tradeType: '',
    location: '',
    minPay: '',
    insuranceRequired: false,
  });
  const [showFilters, setShowFilters] = useState(false);

  const pagination = usePagination(jobs, { pageSize: 12 });

  const pullToRefresh = usePullToRefresh({
    onRefresh: async () => {
      await loadJobs();
    },
  });

  useEffect(() => {
    loadJobs();
  }, [filters]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const params: Record<string, any> = {
        status: 'open',
      };

      if (filters.tradeType) {
        params.trade_type = filters.tradeType;
      }

      if (filters.location) {
        params.location_search = filters.location;
      }

      if (filters.minPay) {
        params.pay_rate_min = parseFloat(filters.minPay);
      }

      if (filters.insuranceRequired) {
        params.insurance_required = true;
      }

      const data = await api.get<Job[]>('/jobs', params);
      setJobs(data || []);
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (jobId: string) => {
    if (!profile) return;

    setApplying(jobId);

    try {
      await api.post('/applications', {
        job_id: jobId,
        contractor_id: profile.id,
        status: 'new',
      });

      setJobs(prev => prev.map(job =>
        job.id === jobId ? { ...job, hasApplied: true } : job
      ));

      alert('Application submitted successfully!');
    } catch (error: any) {
      if (error.code === '23505') {
        alert('You have already applied to this job');
      } else {
        console.error('Error applying:', error);
        alert('Failed to submit application');
      }
    } finally {
      setApplying(null);
    }
  };


  return (
    <div ref={pullToRefresh.containerRef} className="space-y-6">
      <PullToRefreshIndicator
        isPulling={pullToRefresh.isPulling}
        isRefreshing={pullToRefresh.isRefreshing}
        pullDistance={pullToRefresh.pullDistance}
        progress={pullToRefresh.progress}
        shouldTrigger={pullToRefresh.shouldTrigger}
      />
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 rounded-2xl p-8 overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="relative">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Find Jobs</h1>
              <p className="text-slate-300">{jobs.length} open positions available</p>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-lg hover:bg-white/20 transition-all"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filters
              </button>

              <div className="flex bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-2 transition-all ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-white hover:bg-white/10'}`}
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`px-3 py-2 transition-all ${viewMode === 'map' ? 'bg-blue-600 text-white' : 'text-white hover:bg-white/10'}`}
                >
                  <Map className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Trade Type</label>
              <select
                value={filters.tradeType}
                onChange={(e) => setFilters({ ...filters, tradeType: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
              >
                <option value="">All Trades</option>
                {trades.map((trade) => (
                  <option key={trade.id} value={trade.name}>{trade.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
              <input
                type="text"
                placeholder="City or ZIP"
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Min Pay Rate</label>
              <input
                type="number"
                placeholder="$/hr"
                value={filters.minPay}
                onChange={(e) => setFilters({ ...filters, minPay: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Insurance</label>
              <label className="flex items-center space-x-2 py-2">
                <input
                  type="checkbox"
                  checked={filters.insuranceRequired}
                  onChange={(e) => setFilters({ ...filters, insuranceRequired: e.target.checked })}
                  className="w-4 h-4 text-slate-900 border-slate-300 rounded focus:ring-slate-900"
                />
                <span className="text-sm text-slate-700">Required only</span>
              </label>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={() => setFilters({ tradeType: '', location: '', minPay: '', insuranceRequired: false })}
              className="text-sm text-slate-600 hover:text-slate-900"
            >
              Clear filters
            </button>
          </div>
        </div>
      )}

      {profile?.verification_status !== 'verified' && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-xl p-4 shadow-md">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0 shadow-md">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-amber-900">Complete verification to unlock all jobs</h3>
              <p className="text-sm text-amber-700 mt-1">
                Some employers require verified contractors. Complete your profile to access more opportunities.
              </p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <JobCardSkeleton key={i} />
          ))}
        </div>
      ) : viewMode === 'map' ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 h-96 flex items-center justify-center">
          <div className="text-center">
            <Map className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600">Map view coming soon</p>
          </div>
        </div>
      ) : jobs.length === 0 ? (
        <EmptyState
          icon={Briefcase}
          title="No jobs found"
          description="Try adjusting your filters or check back later for new opportunities."
          action={{
            label: 'Clear Filters',
            onClick: () => setFilters({ tradeType: '', location: '', minPay: '', insuranceRequired: false }),
          }}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-h-[calc(100vh-16rem)] overflow-y-auto pr-2">
            {pagination.paginatedItems.map((job) => (
            <div key={job.id} className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-xl hover:border-blue-300 transition-all flex flex-col hover:scale-[1.02]">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="px-2.5 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
                      {job.trade_type}
                    </span>
                    {job.verification_required && (
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" title="Verification required" />
                    )}
                    {job.insurance_required && (
                      <Shield className="w-4 h-4 text-blue-600 flex-shrink-0" title="Insurance required" />
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 line-clamp-2 min-h-[3.5rem]">{job.title}</h3>
                </div>
              </div>

              <p className="text-slate-600 text-sm mb-4 line-clamp-3 min-h-[4.5rem]">{job.description}</p>

              <div className="space-y-2 mb-4 flex-grow">
                <div className="flex items-center text-sm text-slate-600 min-h-[1.25rem]">
                  <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{job.location}</span>
                </div>
                <div className="flex items-center text-sm text-slate-600 min-h-[1.25rem]">
                  <DollarSign className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">${job.pay_rate}/{job.pay_type === 'hourly' ? 'hr' : job.pay_type}</span>
                </div>
                <div className="flex items-center text-sm text-slate-600 min-h-[1.25rem]">
                  <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">
                    {job.start_date ? `Starts ${new Date(job.start_date).toLocaleDateString()}` : 'Start date TBD'}
                  </span>
                </div>
              </div>

              <button
                onClick={() => handleApply(job.id)}
                disabled={applying === job.id}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-3 rounded-lg transition-all mt-auto disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
              >
                {applying === job.id ? 'Applying...' : 'Express Interest'}
              </button>
            </div>
            ))}
          </div>

          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={pagination.goToPage}
            hasNextPage={pagination.hasNextPage}
            hasPreviousPage={pagination.hasPreviousPage}
            getPageNumbers={pagination.getPageNumbers}
            startIndex={pagination.startIndex}
            endIndex={pagination.endIndex}
            totalItems={pagination.totalItems}
          />
        </>
      )}
    </div>
  );
};
